import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { teamAPI, entityAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { AffiliationDisclaimer } from '../components/LegalDisclaimer';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Crown, Star, Eye, List, Lock, MessageSquare, Briefcase } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LiveScoringFeed from '../components/LiveScoringFeed';
import { computePortfolioDelta, formatFranc } from '../lib/utils';
import WorthSparkline from '../components/WorthSparkline';
import XPulseTicker from '../components/XPulseTicker';
import SeasonInfo from '../components/SeasonInfo';
import WeeklyDigest from '../components/WeeklyDigest';
import GameweekBanner from '../components/GameweekBanner';
import TopMoversStrip from '../components/TopMoversStrip';import GameweekCelebration from '../components/GameweekCelebration';
import RecommendationsPanel from '../components/RecommendationsPanel';
import LiveIndicator from '../components/LiveIndicator';
import useScoreFeed from '../hooks/useScoreFeed';
import MiniStandings from '../components/MiniStandings';
import TransferActivity from '../components/TransferActivity';
import NoticeBanner from '../components/NoticeBanner';
import { AnimatedNumber } from '../components/AnimatedNumber';
import QuickActionBar from '../components/QuickActionBar';
import ConfirmDialog from '../components/ConfirmDialog';
import DraftingOverlay from '../components/DraftingOverlay';
import CaptainPickDialog from '../components/CaptainPickDialog';
import { toast } from 'sonner';

import { STAGE_GRID_COLS, STAGE_GRID_ROWS, STAGE_CONFIG, BOOSTER_ICONS, sortRosterSlots, buildArenaLayout, boostedCategories, boosterEndsAt, boosterTimeLeft, formatBoosterEnd } from './stage/stageConfig';
import { StageArenaView } from './stage/StageArenaView';
import { StageListView } from './stage/StageListView';
import { useFeatureFlagVariant } from '../lib/useFeatureFlag';
import { SharedScoreboardsNote } from '../components/SharedScoreboardsNote';
import { LeagueClosedNote } from '../components/LeagueClosedNote';

// 2026-07 A/B (PostHog experiment `stage-empty-cta`) — the primary draft-phase
// CTA that takes a user with an incomplete roster to the Draft Room. This is
// the other big signup→roster_complete drop-off point, tested alongside the
// Starter Picks headline. Ships all variants; split tuned from the dashboard.
const STAGE_CTA_COPY = {
  control: 'Draft Room',
  action: 'Draft your team',
  urgency: 'Start drafting',
};

export default function StageTab({ selectedLeagueId, selectedLeagueName, currentTeam: passedTeam, onTeamUpdate, onManageTeam, onFixEmail, onDraft, onEntityClick, onTeamClick, onTransferMarket, onFormation, onBoosters, onWatchlist, onLeagueChat, onLeagueActivity, leagueSettings }) {
  const { user } = useAuth();
  const stageCtaVariant = useFeatureFlagVariant('stage-empty-cta', 'control');
  // v5.7.61 — Stage view mode (Arena/List) lifecycle:
  //   • DEFAULT to 'graphical' (Arena) every time the user enters
  //     the Stage tab afresh — from another tab, from an app reload,
  //     after sign-out/in, etc.
  //   • PRESERVE the user's choice within a single Stage visit so
  //     entering a person profile / draft room / boosters page and
  //     coming back doesn't snap them off List view.
  // Implementation: regular `useState`. The v5.7.60 fixed-overlay
  // sub-view keeps StageTab mounted across draft/profile/etc, so the
  // state survives those nav cycles. A real tab switch unmounts
  // StageTab, which resets the default to Arena on next entry — exactly
  // the behaviour the user described.
  const [mode, setMode] = useState('graphical');
  const [team, setTeam] = useState(passedTeam);
  const [loading, setLoading] = useState(!passedTeam);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Build 4 — forced captain selection before finalize (no more silent
  // auto-captain surprise). Opens when Finalize is hit with no captain.
  const [showCaptainPick, setShowCaptainPick] = useState(false);
  // 2026-08-31 (owner request) — one-tap smart-random roster fill.
  const [showRandomConfirm, setShowRandomConfirm] = useState(false);
  const [randomBusy, setRandomBusy] = useState(false);
  // 2026-09-07 — OnboardingBanner (a sibling in App.js) hosts the button now.
  useEffect(() => {
    const open = () => setShowRandomConfirm(true);
    window.addEventListener('fws:draft-random', open);
    return () => window.removeEventListener('fws:draft-random', open);
  }, []);
  // 2026-10-12 — OnboardingBanner / FinalizeNowPrompt ask for the Finalize flow.
  const requestFinalizeRef = useRef(null);
  useEffect(() => {
    const ask = () => {
      try { sessionStorage.removeItem('fws_pending_finalize'); } catch (e) { /* private mode */ }
      requestFinalizeRef.current?.();
    };
    window.addEventListener('fws:finalize-request', ask);
    // FinalizeNowPrompt switches here first, so the request can land before mount.
    try { if (sessionStorage.getItem('fws_pending_finalize')) ask(); } catch (e) { /* private mode */ }
    return () => window.removeEventListener('fws:finalize-request', ask);
  }, []);
  const [settingCaptain, setSettingCaptain] = useState(false);

  // v5.7.91 — Unread badge for the League Chat button. Refreshes on
  // league change, on a light 30s poll, and immediately whenever the
  // CommunityChat overlay broadcasts `chat:read` (i.e. the user opened
  // the chat — see CommunityChat.js useEffect).
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    if (!selectedLeagueId) { setChatUnread(0); return undefined; }
    let cancelled = false;
    const fetchUnread = async () => {
      // 2026-07 audit P1 (perf) — skip the poll while the tab is backgrounded;
      // a hidden tab firing an API request every 30s wastes battery/quota and
      // adds needless server load. We refetch immediately on re-focus below.
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await api.get(`/community/${selectedLeagueId}/unread-count`);
        if (!cancelled) setChatUnread(Number(res.data?.unread) || 0);
      } catch (e) {
        // Non-fatal — badge sticks at its last known value until the next
        // poll succeeds. We log so a real outage (e.g. 5xx loop) is still
        // discoverable in the browser console.
        console.warn('[StageTab] unread-count fetch failed:', e?.message || e);
      }
    };
    fetchUnread();
    const poll = setInterval(fetchUnread, 30000);
    // 2026-07-20 perf audit — rapid tab flips re-fired the fetch each time;
    // bound the visibility refetch to once per 10s (the 30s poll still runs).
    let lastVisibleFetch = Date.now();
    const onVisible = () => {
      if (document.hidden) return;
      if (Date.now() - lastVisibleFetch < 10_000) return;
      lastVisibleFetch = Date.now();
      fetchUnread();
    };
    document.addEventListener('visibilitychange', onVisible);
    const onRead = (ev) => {
      if (!ev?.detail?.leagueId || ev.detail.leagueId === selectedLeagueId) {
        setChatUnread(0);
      }
    };
    window.addEventListener('chat:read', onRead);
    return () => {
      cancelled = true;
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('chat:read', onRead);
    };
  }, [selectedLeagueId]);

  // Sync with parent-provided team.
  //
  // v5.7.51 BUGFIX: previously we only setTeam when passedTeam was
  // truthy. So when the user switched to a league where they don't
  // yet have a team, parent set passedTeam=null but our local `team`
  // state kept the OLD team object — making it look like "I'm in
  // League B but my roster is from League A". The fix: ALWAYS sync,
  // including the null/undefined case.
  //
  // 2026-08-12 (owner request) — buffered reveal: when no team was passed but
  // a league IS selected, HOLD the skeleton until loadTeam() resolves so the
  // draft state, roster, budget and today's gain all appear in one paint
  // (previously loading flipped false immediately → empty-state flashed,
  // then the finalized team popped in a beat later).
  useEffect(() => {
    if (passedTeam) { setTeam(passedTeam); setLoading(false); }
    else if (!selectedLeagueId) { setTeam(null); setLoading(false); }
    else { setTeam(null); setLoading(true); }
  }, [passedTeam, selectedLeagueId]);

  // Fallback: load from API if no team passed AND we have a league.
  const loadTeam = useCallback(async () => {
    if (passedTeam) return; // already have it from parent
    if (!selectedLeagueId) return;  // can't pick a team without a league context
    try {
      const res = await teamAPI.myTeams();
      const teams = res.data.teams || [];
      // STRICT match — don't default to teams[0] when the user has no
      // team in the active league. Otherwise switching to a league we
      // haven't joined yet shows the team from the previous league.
      const pick = teams.find(t => t.league_id === selectedLeagueId) || null;
      setTeam(pick);
      if (pick && onTeamUpdate) onTeamUpdate(pick);
    } catch (e) {
      console.error('Failed to load team:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedLeagueId, passedTeam, onTeamUpdate]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  // Sort slots by fixed category order, then alphabetical, then value.
  // PERF (v5.7.37): memoize so live-feed updates (every 2-5s) don't
  // re-sort the array each render. 2026-07-21 — sorter + arena layout
  // builder extracted to stage/stageConfig.js so the public TeamViewPage
  // renders rival rosters in the exact same grid.
  const slots = useMemo(() => sortRosterSlots(team?.slots), [team?.slots]);

  // ─── Dynamic column allocation (v5.7.33 rewrite, memoized v5.7.37) ──────
  // One labeled column per category, spill columns for overflow, faded
  // placeholders up to the league's target shape — full rationale lives
  // with `buildArenaLayout` in stage/stageConfig.js.
  const { slotsWithGrid, columnPlan } = useMemo(
    () => buildArenaLayout(slots, leagueSettings?.slot_config || {}),
    [slots, leagueSettings],
  );

  // 2026-07-21 (owner request) — paper gain/loss across the whole roster.
  const portfolio = useMemo(() => computePortfolioDelta(slots), [slots]);

  // 2026-07-21 (owner request) — roster market-value trend for the tiny
  // Portfolio sparkline. Refetched when the roster composition changes.
  const [worthSeries, setWorthSeries] = useState(null);
  const worthTeamId = team?.id;
  const worthSlotCount = slots.length;
  useEffect(() => {
    if (!worthTeamId) { setWorthSeries(null); return undefined; }
    let cancelled = false;
    teamAPI.valueHistory(worthTeamId)
      .then((res) => { if (!cancelled) setWorthSeries(res.data?.roster_value_series || null); })
      .catch(() => { if (!cancelled) setWorthSeries(null); });
    return () => { cancelled = true; };
  }, [worthTeamId, worthSlotCount]);

  // ─── Real-time score floaters ─────────────────────────────────────────────
  // Subscribe to backend SSE; emit a tier-sized "+₣ N.NN" floater above any
  // person on this roster when scoring events flush from the EventDistribution
  // Buffer. Stable across renders: depend on a sorted ID string so we don't
  // tear down the EventSource every time `slots` is re-derived in render.
  const entityIdKey = useMemo(() => slots.map(s => s.entity_id).sort().join(','), [slots]);
  const entityIdSet = useMemo(() => {
    return new Set(entityIdKey ? entityIdKey.split(',') : []);
    // Deliberately depend on the STRING, not the Set object
  }, [entityIdKey]);
  const { floaters, status: feedStatus, eventsTotal, lastEventAt } = useScoreFeed(entityIdSet);

  // group floaters by entity_id for fast lookup during render
  const floatersByEntity = useMemo(() => {
    const out = {};
    for (const f of floaters) {
      (out[f.entity_id] = out[f.entity_id] || []).push(f);
    }
    return out;
  }, [floaters]);

  // Optimistic local accumulator: live daily score per entity, additive on
  // each floater. Cleared when team data reloads.
  const [liveBumps, setLiveBumps] = useState({});
  const seenIdsRef = useRef(new Set());
  useEffect(() => {
    setLiveBumps({}); seenIdsRef.current = new Set();
  }, [team?.id]);
  useEffect(() => {
    if (!floaters.length) return;
    const fresh = floaters.filter((f) => !seenIdsRef.current.has(f.id));
    if (!fresh.length) return;
    setLiveBumps((prev) => {
      const next = { ...prev };
      for (const f of fresh) {
        next[f.entity_id] = (next[f.entity_id] || 0) + f.points;
        seenIdsRef.current.add(f.id);
      }
      return next;
    });
  }, [floaters]);

  // Helper: live-adjusted daily score for an entity (server-known + optimistic bumps)
  // 2026-07-20 perf audit — stable identities: both are passed as props to the
  // memoized Arena/List views; fresh arrows every render defeated their memo.
  const liveDaily = useCallback((slot) => {
    const base = slot.entity?.stats?.daily_points || slot.entity?.stats?.weekly_points || 0;
    const bump = liveBumps[slot.entity_id] || 0;
    return base + bump;
  }, [liveBumps]);
  // Detect "huge" tier hits to flash the avatar
  const hasHugeFloater = useCallback(
    (eid) => (floatersByEntity[eid] || []).some(f => Math.abs(f.points) >= 15),
    [floatersByEntity],
  );

  // v5.7.81 — Aggregate `liveBumps` across the entire roster so the
  // "Today" stat card ticks up in real time alongside each floater. Sum
  // ONLY the bumps for entities actually on this roster (liveBumps may
  // also contain values for entities we've since removed during draft).
  const todayBump = useMemo(() => {
    let total = 0;
    for (const s of slots) {
      total += liveBumps[s.entity_id] || 0;
    }
    return total;
  }, [liveBumps, slots]);
  // Display value: server-known DAILY counter (reset 00:00 UTC) + live deltas.
  const todayDisplay = (team?.daily_points || 0) + todayBump;
  
  // Team finalization status
  const isFinalized = team?.finalized || false;
  
  // League-specific slot total
  const _slotConfig = leagueSettings?.slot_config || {};
  const _hasCustomSlots = Object.keys(_slotConfig).length > 0;
  const totalNeeded = _hasCustomSlots
    ? Object.values(_slotConfig).reduce((a, b) => a + b, 0)
    : Math.min((leagueSettings?.allowed_categories?.length || 6) * (leagueSettings?.required_per_category || 3), 18);
  const rosterFull = slots.length >= totalNeeded;
  // 2026-10-12 Activation Fix Pack — go live with 1+ picks; open slots become
  // fee-free "free fills" (server stamps free_fills_remaining at Finalize).
  const canFinalize = slots.length >= 1 && !isFinalized;
  const openSlots = Math.max(0, totalNeeded - slots.length);
  const freeFills = isFinalized ? Math.max(0, Number(team?.free_fills_remaining) || 0) : 0;
  const hasCaptain = slots.some((s) => s.is_captain);

  // Build 4 — Finalize entry point: force a captain pick first when none set.
  const requestFinalize = () => {
    if (!canFinalize) return;
    if (!hasCaptain) setShowCaptainPick(true);
    else setShowFinalizeConfirm(true);
  };

  requestFinalizeRef.current = requestFinalize;

  const handleCaptainPicked = async (entityId) => {
    if (!team?.id || !entityId) return;
    setSettingCaptain(true);
    try {
      await teamAPI.setCaptain({ team_id: team.id, entity_id: entityId });
      setTeam((prev) => prev ? {
        ...prev,
        slots: (prev.slots || []).map((s) => ({ ...s, is_captain: s.entity_id === entityId })),
      } : prev);
      setShowCaptainPick(false);
      toast.success('Captain locked in — they earn 2× on every ₣!');
      setShowFinalizeConfirm(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set captain');
    } finally { setSettingCaptain(false); }
  };

  const handleFinalize = async () => {
    if (!team?.id) return;
    setFinalizing(true);
    try {
      const res = await teamAPI.finalize(team.id);
      // Reload from API so the auto-assigned captain comes back too.
      // 2026-07 UX audit P1 — find THIS team (teams[0] showed the wrong
      // team's state for multi-league users).
      const my = await teamAPI.myTeams();
      const fresh = my.data?.teams?.find(t => t.id === team.id) || null;
      if (fresh) {
        setTeam(fresh);
        if (onTeamUpdate) onTeamUpdate(fresh);
      } else {
        setTeam(prev => {
          const next = prev ? { ...prev, finalized: true } : prev;
          if (next && onTeamUpdate) onTeamUpdate(next);
          return next;
        });
      }
      toast.success(res?.data?.message || 'Team finalized! You are now competing.');
      // If the backend auto-assigned a captain, surface it so the user
      // can immediately pick a different person if they want to.
      if (res?.data?.default_captain_assigned) {
        const who = res.data.default_captain_name || 'your first-drafted person';
        toast(
          `Captain auto-set to ${who}`,
          {
            description: 'They get the 2× score multiplier. Tap any roster slot on Stage to change.',
            duration: 8000,
          }
        );
      }
      setShowFinalizeConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to finalize');
    } finally { setFinalizing(false); }
  };
  
  // Stage style backgrounds — local images with per-style entity positioning.
  // PERF (v5.7.37): read localStorage ONCE on mount (lazy useState init)
  // instead of on every render.
  // v5.7.39: default for new users / guests is now the Shakespearean theater
  // (was colosseum). Users who've previously picked a style keep their pick.
  const [stageStyle] = useState(() => {
    if (typeof window === 'undefined') return 'theater';
    try { return localStorage.getItem('stage_style') || 'theater'; } catch { return 'theater'; }
  });
  const stageConfig = STAGE_CONFIG[stageStyle] || STAGE_CONFIG.theater;

  // 2026-09-07 — column headers wear a "2x" badge for the boosted category
  // (hook stays above the early returns below).
  const boostedCats = useMemo(() => boostedCategories(team?.active_boosters), [team?.active_boosters]);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-3xl" />
        <div className="space-y-3">
          {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}
        </div>
      </div>
    );
  }

  // Active boosters display (icons hoisted to module scope for perf)
  const activeBoosters = team?.active_boosters || [];

  // iter_189 — explicit empty state instead of a silent ghost arena when the
  // selected league has no team. "Set up my team" hits /teams/my, which
  // self-heals missing global-league teams server-side.
  if (!team?.id) {
    return (
      <div className="p-4" data-testid="stage-tab">
        <div className="rounded-2xl border border-dashed border-border/70 bg-card p-8 text-center" data-testid="stage-no-team-state">
          <p className="text-sm font-semibold mb-1">No team in this league yet</p>
          <p className="text-xs text-muted-foreground mb-4">Set one up and start drafting your 18.</p>
          <Button
            onClick={() => {
              try { window.dispatchEvent(new CustomEvent('fws:team-refresh')); } catch (e) { /* noop */ }
              loadTeam();
            }}
            data-testid="stage-no-team-retry-btn"
          >
            Set up my team
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="stage-tab">
      {/* 2026-09-03 — per-user notices (e.g. drop-fee refund) until dismissed */}
      <NoticeBanner onAction={(view) => {
        if (view === 'manage-team') onManageTeam?.();
        else if (view === 'fix-email') onFixEmail?.();
        else if (view === 'draft-room') onDraft?.();
      }} />
      {/* Mode Toggle + Active Boosters */}
      <div className="flex items-center justify-between mb-4">
        {/* Active Boosters (left) */}
        <div className="flex items-center gap-1" data-testid="stage-active-boosters">
          {activeBoosters.map((b, i) => {
            const info = BOOSTER_ICONS[b] || { emoji: '⚡', label: b };
            // v5.7.92 — Key by the booster id itself (e.g. "captain_boost")
            // so React can correctly track add/remove when a booster expires
            // mid-session. The `${b}-${i}` suffix defends against the rare
            // case of the same booster appearing twice in an array (e.g.
            // stacked time-limited buffs in a future iteration).
            // 2026-09-08 — "· 6d 3h" countdown from the stored 7-day end.
            const endsAt = boosterEndsAt(null, team?.booster_expiry?.[b]);
            const left = boosterTimeLeft(endsAt);
            return (
              <div key={`${b}-${i}`} className="flex items-center gap-0.5 bg-[hsl(var(--accent)/0.15)] rounded-full px-2 py-0.5" title={endsAt ? `${info.label} — active until ${formatBoosterEnd(endsAt)}` : info.label} data-testid={`stage-booster-chip-${b}`}>
                <span className="text-sm">{info.emoji}</span>
                <span className="text-[8px] font-semibold text-[hsl(var(--accent-text))]">
                  {info.label}{left && left !== 'expired' ? ` · ${left}` : ''}
                </span>
              </div>
            );
          })}
        </div>
        {/* Mode Toggle (right) */}
        <div className="flex items-center gap-2">
          <LiveIndicator status={feedStatus} eventsTotal={eventsTotal} lastEventAt={lastEventAt} />
          <Tabs value={mode} onValueChange={setMode} data-testid="stage-mode-tabs">
            <TabsList className="h-8">
              <TabsTrigger value="graphical" className="text-xs gap-1 px-3" data-testid="stage-mode-graphical">
                <Eye className="h-3 w-3" /> Arena
              </TabsTrigger>
              <TabsTrigger value="list" className="text-xs gap-1 px-3" data-testid="stage-mode-list">
                <List className="h-3 w-3" /> List
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {mode === 'graphical' ? (
          <motion.div
            key="graphical"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
          >
            {/* Arena View — Rectangular grid (6 categories x up to 3 rows) */}
            <StageArenaView
              stageConfig={stageConfig}
              columnPlan={columnPlan}
              slotsWithGrid={slotsWithGrid}
              floatersByEntity={floatersByEntity}
              liveDaily={liveDaily}
              hasHugeFloater={hasHugeFloater}
              onEntityClick={onEntityClick}
              onDraft={onDraft}
              boostedCategories={boostedCats}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
          >
            {/* List View — grouped by category with headers */}
            <StageListView
              slots={slots}
              floatersByEntity={floatersByEntity}
              liveDaily={liveDaily}
              hasHugeFloater={hasHugeFloater}
              onEntityClick={onEntityClick}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Team Summary */}
      {team && (
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="bg-card border border-border/60 rounded-xl p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Roster</p>
            <p className="text-2xl font-bold tabular-nums mt-1" data-testid="stat-roster">{slots.length}/{totalNeeded}</p>
          </div>
          <div className="bg-card border border-border/60 rounded-xl p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Budget</p>
            <p className="text-2xl font-bold tabular-nums mt-1" data-testid="stat-budget">
              <AnimatedNumber
                value={team.budget_remaining || 0}
                snapshotKey={`balance:${selectedLeagueId || 'global'}:${user?.id || 'anon'}`}
                durationMs={1100}
                format={(n) => formatFranc(n)}
                testid="stat-budget-value"
              />
            </p>
          </div>
          <div className="bg-card border border-border/60 rounded-xl p-4 text-center" title="Points earned since midnight UTC — resets daily">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Today</p>
            <p
              className="text-2xl font-bold tabular-nums mt-1 text-[hsl(var(--positive-text))]"
              data-testid="stat-today"
            >
              <AnimatedNumber
                value={todayDisplay}
                snapshotKey={`stagetoday:${selectedLeagueId || 'global'}:${user?.id || 'anon'}`}
                durationMs={1100}
                format={(n) => formatFranc(n, { sign: true })}
                testid="stat-today-value"
              />
            </p>
          </div>
        </div>
      )}

      {/* Portfolio — paper gain/loss since purchase (2026-07-21).
          Build 4 — finalized teams only: pre-finalize, Net Worth is frozen
          at acquired prices, so a market-drift delta here told a
          contradictory story. */}
      {team && portfolio && isFinalized && (
        <div
          className="mt-3 flex items-center justify-center gap-2 bg-card border border-border/60 rounded-xl px-4 py-2.5"
          data-testid="stat-portfolio"
        >
          <Briefcase className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground uppercase tracking-wide">Portfolio</span>
          <span
            className={`text-sm font-bold tabular-nums whitespace-nowrap ${portfolio.delta >= 0 ? 'text-[hsl(var(--positive-text))]' : 'text-destructive'}`}
            data-testid="stat-portfolio-delta"
          >
            {portfolio.delta >= 0 ? '▲' : '▼'} {formatFranc(Math.abs(portfolio.delta))}
          </span>
          <span className="text-xs text-muted-foreground">
            since purchase · {portfolio.count} {portfolio.count === 1 ? 'person' : 'people'}
          </span>
          <WorthSparkline series={worthSeries} />
        </div>
      )}

      {/* Action Buttons — Phase-aware; a closed league shows its final-record note */}
      {team?.frozen ? (
        <LeagueClosedNote team={team} testid="stage-league-closed-note" className="mt-4" />
      ) : (
      <div className="mt-4">
        <SharedScoreboardsNote team={team} testid="stage-shared-scoreboards-note" className="mb-2" />
        {isFinalized ? (
          /* GAME PHASE: Manage Team (+ Draft CTA when a post-finalize drop
             left open slots — iter_181: the empty silhouette was the only,
             undiscoverable way back into the Draft Room). */
          <>
            {!rosterFull && (
              <Button
                className="w-full mb-2"
                onClick={onDraft}
                data-testid="stage-draft-btn"
              >
                <Star className="h-4 w-4 mr-2" /> Draft Room — Fill {openSlots} Open Slot{openSlots === 1 ? '' : 's'}{freeFills > 0 ? ' (free)' : ''}
              </Button>
            )}
            {!rosterFull && freeFills > 0 && (
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400 text-center mb-2" data-testid="stage-free-fills-hint">
                You're live with {slots.length}/{totalNeeded}. {freeFills} open slot{freeFills === 1 ? '' : 's'} fill for the player's price only — no roster-move fee.
              </p>
            )}
            <Button
              className="w-full"
              variant={rosterFull ? 'default' : 'outline'}
              onClick={onManageTeam}
              data-testid="stage-manage-btn"
            >
              <Crown className="h-4 w-4 mr-2" /> Manage Team
            </Button>
          </>
        ) : (
          /* DRAFT PHASE: Draft Room + Finalize Team (+ Manage, Build 4) */
          <>
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={onDraft}
                data-testid="stage-draft-btn"
              >
                <Star className="h-4 w-4 mr-2" /> {STAGE_CTA_COPY[stageCtaVariant] || STAGE_CTA_COPY.control}
              </Button>
              <Button
                variant={canFinalize ? 'default' : 'outline'}
                className={`flex-1 ${!canFinalize ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={requestFinalize}
                disabled={!canFinalize}
                data-testid="stage-finalize-btn"
              >
                <Lock className="h-4 w-4 mr-2" /> {rosterFull ? 'Finalize Team' : 'Go Live Now'}
              </Button>
            </div>
            {canFinalize && !rosterFull && (
              <p className="text-[11px] text-muted-foreground text-center mt-2" data-testid="stage-go-live-hint">
                {slots.length}/{totalNeeded} picked — you can go live now and fill the other {openSlots} later, free of any fee.
              </p>
            )}
            {/* 2026-09-07 (owner request) — the Draft Random button moved up
                under "Go to Draft Room" in OnboardingBanner (App.js); it
                asks via the 'fws:draft-random' event handled below. */}
            {/* Build 4 — Team management (captain pills, drops) was
                unreachable before finalize; expose it once anyone is rostered. */}
            {slots.length > 0 && (
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={onManageTeam}
                data-testid="stage-manage-btn"
              >
                <Crown className="h-4 w-4 mr-2" /> Manage Team
              </Button>
            )}
          </>
        )}
      </div>
      )}

      {/* Build 4 — mandatory captain selection before finalize */}
      <CaptainPickDialog
        open={showCaptainPick}
        slots={slots}
        busy={settingCaptain}
        onConfirm={handleCaptainPicked}
        onCancel={() => setShowCaptainPick(false)}
      />

      {/* 2026-08-31 — smart-random fill confirm */}
      <ConfirmDialog
        open={showRandomConfirm}
        title="Draft a random team?"
        description={`We'll fill your remaining ${totalNeeded - slots.length} slot${totalNeeded - slots.length === 1 ? '' : 's'} with a smart-random mix that spends most of your budget. You can still swap anyone before finalizing.`}
        confirmLabel="Draft Random"
        onConfirm={async () => {
          setShowRandomConfirm(false);
          setRandomBusy(true);
          try {
            const res = await teamAPI.draftRandom({ team_id: team.id, league_id: selectedLeagueId || team.league_id });
            toast.success(res.data?.message || 'Random team drafted!');
          } catch (e) {
            const d = e.response?.data?.detail;
            // Self-heal: "already full" means our UI was stale — the refetch
            // below (+ the wrapper's fws:team-refresh) resyncs it.
            if (typeof d === 'string' && d.toLowerCase().includes('already full')) toast.info(d);
            else toast.error(typeof d === 'string' ? d : 'Random draft failed — please try again');
          } finally {
            // ALWAYS resync — the api wrapper fires fws:team-refresh (App
            // refetches passedTeam); loadTeam covers the no-passedTeam path.
            try { await loadTeam(); } catch (e2) { /* best-effort */ }
            setRandomBusy(false);
          }
        }}
        onCancel={() => setShowRandomConfirm(false)}
      />
      <DraftingOverlay visible={randomBusy} />

      {/* Finalize confirmation */}
      <ConfirmDialog
        open={showFinalizeConfirm}
        title={rosterFull ? 'Finalize Your Team' : `Go live with ${slots.length} of ${totalNeeded}?`}
        description={rosterFull
          ? 'Lock in your team to start competing. After finalizing, adding a person counts as a roster move — your first add each week is free, then an escalating budget fee applies (2nd: 2%, 3rd+: 5%+, capped at 90%). Dropping someone is always free and refunds their current market price.'
          : `You enter the rankings right away and your ${openSlots} open slot${openSlots === 1 ? '' : 's'} stay${openSlots === 1 ? 's' : ''} free to fill any time — you pay the player's price only, no roster-move fee. Swapping someone already on your team counts as a roster move (first each week free, then 2%, 5%+). Dropping is always free.`}
        confirmLabel={rosterFull ? 'Finalize Team' : 'Go Live'}
        cancelLabel="Keep Drafting"
        onConfirm={handleFinalize}
        onCancel={() => setShowFinalizeConfirm(false)}
      />

      {/* Quick Actions */}
      <div className="mt-4">
        <QuickActionBar onAction={(id) => {
          if (id === 'formation') onFormation?.();
          else if (id === 'market') onTransferMarket?.();
          else if (id === 'boosters') onBoosters?.();
          else if (id === 'transfer') onManageTeam?.();
          else if (id === 'watchlist') onWatchlist?.();
        }} />
      </div>

      {/* 2026-07 — Top Movers strip (PostHog `top-movers` flag) */}
      <TopMoversStrip onEntityClick={onEntityClick} />

      {/* Mini Rankings */}
      <div className="mt-4">
        <MiniStandings selectedLeagueId={selectedLeagueId} onTeamClick={onTeamClick} />
      </div>

      {/* Gameweek Banner */}
      <div className="mt-4">
        <GameweekCelebration team={team} />
        <GameweekBanner team={team} rosterFull={rosterFull} />
      </div>

      {/* Season Info */}
      <div className="mt-4">
        <SeasonInfo />
      </div>

      {/* Weekly Digest */}
      <div className="mt-4">
        <WeeklyDigest onEntityClick={onEntityClick} />
      </div>

      {/* X Pulse — social heartbeat (mentions are excluded from Live Scoring) */}
      <XPulseTicker onEntityClick={onEntityClick} />

      {/* Live Scoring Feed */}
      <div className="mt-4">
        <LiveScoringFeed onEntityClick={onEntityClick} />
      </div>

      {/* 2026-09-03 (owner request) — per-league Transfer Activity feed, at the
          bottom of Stage directly above League Chat so activity + chat sit
          together (community "life meter"). */}
      <div className="mt-4">
        <TransferActivity
          selectedLeagueId={selectedLeagueId}
          onTeamClick={onTeamClick}
          onEntityClick={onEntityClick}
          onSeeAll={onLeagueActivity}
        />
      </div>

      {/* v5.7.89 — League Chat button. 2026-09-03: moved to the bottom of the
          Stage (mobile parity) so it sits right under Transfer Activity. Label
          uses the league name so users know exactly which chat they're
          entering; hidden while a league is still loading. */}
      {selectedLeagueId && onLeagueChat && (
        <div className="mt-4">
          <Button
            className="w-full"
            onClick={onLeagueChat}
            data-testid="league-chat-button"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            {selectedLeagueName ? `${selectedLeagueName} Chat` : 'League Chat'}
            {chatUnread > 0 && (
              <Badge
                variant="secondary"
                className="ml-2 h-5 min-w-[20px] px-1.5 rounded-full text-[10px] font-semibold bg-background text-foreground border border-border/40"
                data-testid="league-chat-unread-badge"
              >
                {chatUnread > 99 ? '99+' : chatUnread} new
              </Badge>
            )}
          </Button>
        </div>
      )}

      {/* Legal Disclaimer — visible when scrolled to bottom */}
      <div className="mt-6 mb-2">
        <AffiliationDisclaimer testId="stage-affiliation-disclaimer" />
      </div>
    </div>
  );
}
